import * as React from 'react'
import {
  CheckCircle,
  CircleNotch,
  Info,
  Warning,
  X,
  XCircle,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Avatar, type AvatarProps } from './avatar'

/**
 * Toast — floating notification card, from the TailGrids toast model + the Figma
 * toast specs. White surface (border + shadow), a status icon, a message, and an
 * optional close / undo / actions. `AvatarToast` is the message-style variant.
 *
 *   variant  default | success | error | warning | info | loading
 *   size     md | sm
 *   message  string  ·  { title, description }  (string → single row;
 *            object → stacked title + description with optional action children)
 *
 * On the white surface the status icons clear the 3:1 non-text bar (unlike the
 * tinted alerts). `onClose` adds a × button; `undoAction` adds an Undo link.
 *
 * Actions (in `children`): use a neutral outline <Button appearance="outline">
 * + a <Link>, matching Alert — never status-colored fills.
 */

type ToastVariant =
  | 'default'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'loading'
type ToastSize = 'md' | 'sm'

const iconFor: Record<ToastVariant, Icon> = {
  default: Info,
  success: CheckCircle,
  error: XCircle,
  warning: Warning,
  info: Info,
  loading: CircleNotch,
}

const iconColor: Record<ToastVariant, string> = {
  default: 'text-fbui-gray-500',
  success: 'text-fbui-green-600',
  error: 'text-fbui-red-600',
  // yellow-700, not -600: yellow-600 on white is 2.94:1, below the 3:1 floor.
  warning: 'text-fbui-yellow-700',
  info: 'text-fbui-sky-600',
  loading: 'text-fbui-gray-400',
}

const cardCls =
  'relative flex rounded-lg border border-fbui-gray-200 bg-fbui-base-white shadow-fbui-toast'

function ToastClose({
  size,
  onClose,
}: {
  size: ToastSize
  onClose: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Dismiss"
      className="-m-1 inline-flex shrink-0 items-center justify-center rounded-full p-1 text-fbui-gray-500 outline-none transition-colors hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-default"
    >
      <X
        weight="bold"
        className={size === 'sm' ? 'size-4' : 'size-5'}
        aria-hidden="true"
      />
    </button>
  )
}

export interface ToastProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  variant?: ToastVariant
  size?: ToastSize
  /** A plain string (single row) or a title + description (stacked). */
  message: string | { title: string; description: string }
  /** Renders a trailing × button. */
  onClose?: () => void
  /** Renders an inline "Undo" link (string messages only). */
  undoAction?: () => void
  /** Generic inline text link (e.g. "View"), shown before the close button. */
  action?: { label: string; onClick: () => void }
  /** Hide the status icon. */
  hideIcon?: boolean
  /** Override the default status icon. */
  icon?: React.ReactNode
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      message,
      onClose,
      undoAction,
      action,
      hideIcon,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    const structured = typeof message !== 'string'
    const StatusIcon = iconFor[variant]
    const indicator = hideIcon ? null : (
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0 [&>svg]:block',
          structured ? '' : 'mt-px',
          size === 'sm' ? '[&>svg]:size-5' : '[&>svg]:size-6',
          iconColor[variant]
        )}
      >
        {icon ??
          (variant === 'loading' ? (
            <CircleNotch className="animate-spin" />
          ) : (
            <StatusIcon weight="fill" />
          ))}
      </span>
    )

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          cardCls,
          structured ? 'items-start' : 'items-center',
          size === 'sm' ? 'gap-2.5 p-2.5' : 'gap-3 p-3',
          className
        )}
        {...props}
      >
        {indicator}
        {structured ? (
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-col',
              size === 'sm' ? 'gap-1' : 'gap-1.5'
            )}
          >
            <p
              className={cn(
                'font-semibold tracking-[-0.2px] text-fbui-text-title',
                size === 'sm' ? 'text-sm' : 'text-base'
              )}
            >
              {message.title}
            </p>
            <p
              className={cn(
                'tracking-[-0.2px] text-fbui-text-secondary',
                size === 'sm' ? 'text-xs' : 'text-sm'
              )}
            >
              {message.description}
            </p>
            {children && (
              <div className="mt-1 flex items-center gap-2">{children}</div>
            )}
          </div>
        ) : (
          <>
            <p
              className={cn(
                'min-w-0 flex-1 font-medium tracking-[-0.2px] text-fbui-text-title',
                size === 'sm' ? 'text-sm' : 'text-base'
              )}
            >
              {message}
            </p>
            {(action || undoAction) && (
              <button
                type="button"
                onClick={action ? action.onClick : undoAction}
                className={cn(
                  'shrink-0 font-medium tracking-[-0.2px] text-fbui-basil-600 outline-none transition-colors hover:text-fbui-basil-700 focus-visible:underline',
                  size === 'sm' ? 'text-xs' : 'text-sm'
                )}
              >
                {action ? action.label : 'Undo'}
              </button>
            )}
          </>
        )}
        {onClose && <ToastClose size={size} onClose={onClose} />}
      </div>
    )
  }
)
Toast.displayName = 'Toast'

export interface AvatarToastProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  size?: ToastSize
  /** Sender name. */
  name: string
  /** Message body. */
  description: string
  /** Avatar image URL. */
  image?: string
  /** Presence dot on the avatar. */
  status?: AvatarProps['status']
  /** Timestamp label (rendered in the brand color). */
  time?: string
  onClose?: () => void
}

export const AvatarToast = React.forwardRef<HTMLDivElement, AvatarToastProps>(
  (
    {
      className,
      size = 'md',
      name,
      description,
      image,
      status,
      time,
      onClose,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className={cn(
        cardCls,
        'items-start',
        size === 'sm' ? 'gap-2.5 p-2.5' : 'gap-3 p-3',
        className
      )}
      {...props}
    >
      <Avatar
        size={size === 'sm' ? 'md' : 'lg'}
        src={image}
        name={name}
        status={status}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p
          className={cn(
            'font-semibold tracking-[-0.2px] text-fbui-text-title',
            size === 'sm' ? 'text-sm' : 'text-base'
          )}
        >
          {name}
        </p>
        <p
          className={cn(
            'tracking-[-0.2px] text-fbui-text-secondary',
            size === 'sm' ? 'text-xs' : 'text-sm'
          )}
        >
          {description}
        </p>
        {time && (
          <p className="text-xs font-medium text-fbui-basil-600">{time}</p>
        )}
      </div>
      {onClose && <ToastClose size={size} onClose={onClose} />}
    </div>
  )
)
AvatarToast.displayName = 'AvatarToast'
